import { ProtocolFormSelection, ProtocolVisitForms } from '../../../../models/Entities/Protocols/ProtocolVisitForms';

/** Opción del selector: un formulario con su visita, con una clave única para el TagPicker. */
export interface ProtocolFormOption extends ProtocolFormSelection {
  /** `${visitId}:${formId}`: el mismo formulario puede repetirse en más de una visita. */
  key: string;
  visitOrder: number;
  formOrder: number;
}

export const optionKey = (visitId: number, formId: number): string => `${visitId}:${formId}`;

/** Aplana la respuesta del servidor en opciones, en orden de visita y de formulario dentro de cada una. */
export const flattenProtocolForms = (visits: ProtocolVisitForms[] | undefined | null): ProtocolFormOption[] => {
  const options: ProtocolFormOption[] = [];
  [...(visits ?? [])]
    .sort((a, b) => a.visitOrder - b.visitOrder || a.visitId - b.visitId)
    .forEach((visit) => {
      [...(visit.forms ?? [])]
        .sort((a, b) => a.order - b.order || a.formId - b.formId)
        .forEach((form) => {
          options.push({
            key: optionKey(visit.crossVersionId, form.formId),
            formId: form.formId,
            formTitle: form.title,
            visitId: visit.crossVersionId,
            visitTitle: visit.visitTitle,
            visitOrder: visit.visitOrder,
            formOrder: form.order,
          });
        });
    });
  return options;
};

/** Texto de la opción: "V601 · Laboratorio Lp(a) 601". */
export const optionLabel = (option: Pick<ProtocolFormOption, 'visitTitle' | 'formTitle'>): string =>
  option.visitTitle ? `${option.visitTitle} · ${option.formTitle}` : option.formTitle;

/** Filtro del cuadro de búsqueda: por título del formulario o de la visita, sin acentos ni mayúsculas. */
export const normalizeText = (value: string | undefined | null): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

export const filterOptions = (options: ProtocolFormOption[], query: string, excludeKeys: ReadonlySet<string> = new Set()): ProtocolFormOption[] => {
  const needle = normalizeText(query);
  return options.filter((option) => {
    if (excludeKeys.has(option.key)) return false;
    if (!needle) return true;
    return normalizeText(option.formTitle).includes(needle) || normalizeText(option.visitTitle).includes(needle);
  });
};

/**
 * Opción que corresponde a lo guardado. Si la visita guardada ya no existe (otra versión de la plantilla) se
 * busca por formId solo, y si tampoco está, se arma una opción "huérfana" con el título guardado para no
 * perder lo que el usuario había elegido.
 */
export const resolveSelected = (
  options: ProtocolFormOption[],
  selected: Pick<ProtocolFormSelection, 'formId' | 'visitId' | 'formTitle' | 'visitTitle'> | undefined | null
): ProtocolFormOption | undefined => {
  if (!selected || !selected.formId) return undefined;
  const exact = options.find((option) => option.formId === selected.formId && option.visitId === selected.visitId);
  if (exact) return exact;
  const byForm = options.find((option) => option.formId === selected.formId);
  if (byForm) return byForm;
  return {
    key: optionKey(selected.visitId ?? 0, selected.formId),
    formId: selected.formId,
    formTitle: selected.formTitle || `#${selected.formId}`,
    visitId: selected.visitId ?? 0,
    visitTitle: selected.visitTitle ?? '',
    visitOrder: Number.MAX_SAFE_INTEGER,
    formOrder: Number.MAX_SAFE_INTEGER,
  };
};
